import { useAuth } from "@lib/auth/useAuth";
import { useIsLoyaltyMember } from "./useIsLoyaltyMember";
import { useGlobalContent } from "@app/[locale]/GlobalContentProvider";
import { useMemo, useState, useEffect } from "react";
import type { Product } from "@lib/product/types";
import { checkHasEccoClub } from "@lib/utils/loyalty";

/**
 * useProductAccess
 * A stable access state machine that determines if a user can buy a product
 * or if they need to join the ECCO Club first.
 *
 * Protects against environment noise and re-render flickering.
 */
export const useProductAccess = (product: Product) => {
    const { status } = useAuth();
    const { isLoyaltyMember, isLoading: isLoadingLoyalty } = useIsLoyaltyMember();
    const { siteSettings } = useGlobalContent();

    const isSaleSeason = !!siteSettings.promotions.uspBanner?.isSaleSeason;

    const hasEccoClub = useMemo(
        () => checkHasEccoClub(product.productAttributes),
        [product.productAttributes]
    );

    // 1. Determine the raw access phase
    const accessPhase = useMemo(() => {
        const isSessionLoading = status === "loading";
        const isProfileLoading = status === "authenticated" && isLoadingLoyalty;
        const isGuest = status === "unauthenticated";
        const isMember = status === "authenticated" && isLoyaltyMember;

        if (isSessionLoading || isProfileLoading) return "loading";

        // Sale Season Rule: Guests are restricted to CTA
        if (isSaleSeason) return isGuest ? "restricted" : "allowed";

        // Ecco Club Rule: Club products require membership
        if (hasEccoClub && !isMember) return "restricted";

        return "allowed";
    }, [status, isLoadingLoyalty, hasEccoClub, isSaleSeason, isLoyaltyMember]);

    // 2. Sticky state to prevent flickering during re-renders
    const [stablePhase, setStablePhase] = useState<"loading" | "restricted" | "allowed">("loading");

    useEffect(() => {
        if (accessPhase !== "loading") {
            setStablePhase(accessPhase);
        }
    }, [accessPhase]);

    return {
        showEccoClubCTA: stablePhase === "restricted",
        showAddToCartButton: stablePhase !== "restricted",
        isAccessUncertain: accessPhase === "loading",
        isAuthenticated: status === "authenticated",
        status,
        hasEccoClub,
    };
};
